/**
 * BrandSelector — pills (≤2 brands) or dropdown (≥3 brands) per spec §2.4.6.
 *
 * Renders inline in the NavBar. Selecting a brand:
 *   - calls `setActiveBrand(brand)` (writes the `pe_brand` cookie)
 *   - navigates to the same logical page under the new brand prefix
 *
 * Logical-page navigation is naive but predictable: replace the leading
 * `/{oldBrand}` in the current URL with `/{newBrand}`. If the current
 * path doesn't have a brand prefix, navigate to `/{newBrand}/cars`.
 */

import { useLocation, useNavigate } from "react-router-dom";
import { useBrandContext } from "../context/BrandContext";

const PILL_THRESHOLD = 2;

function rewriteBrandPrefix(
  pathname: string,
  oldBrand: string,
  newBrand: string,
): string {
  if (pathname === "/" || pathname === "") return `/${newBrand}/cars`;
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return `/${newBrand}/cars`;
  if (segments[0] === oldBrand) {
    segments[0] = newBrand;
    return `/${segments.join("/")}`;
  }
  // The current path is brand-agnostic (login, filters, about/…) — keep the
  // user where they are and just update the cookie.
  return pathname;
}

export default function BrandSelector() {
  const { brands, activeBrand, setActiveBrand, isLoading } = useBrandContext();
  const navigate = useNavigate();
  const location = useLocation();

  if (isLoading || brands.length === 0) return null;
  if (brands.length === 1) return null; // no choice to make — hide entirely

  const onSelect = (brand: string) => {
    if (brand === activeBrand) return;
    setActiveBrand(brand);
    const next = rewriteBrandPrefix(location.pathname, activeBrand, brand);
    navigate(next, { replace: false });
  };

  if (brands.length <= PILL_THRESHOLD) {
    return (
      <div
        role="radiogroup"
        aria-label="Brand"
        className="flex items-center gap-1 mr-4"
      >
        {brands.map((b) => {
          const active = b.brand_code === activeBrand;
          return (
            <button
              key={b.brand_code}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onSelect(b.brand_code)}
              className={`px-3 py-1 rounded text-sm ${
                active
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {b.display_name}
            </button>
          );
        })}
      </div>
    );
  }

  // ≥3 brands: dropdown
  return (
    <select
      aria-label="Brand"
      value={activeBrand}
      onChange={(e) => onSelect(e.target.value)}
      className="mr-4 border rounded px-2 py-1 text-sm bg-white"
    >
      {brands.map((b) => (
        <option key={b.brand_code} value={b.brand_code}>
          {b.display_name}
        </option>
      ))}
    </select>
  );
}
